import { Formik, useField } from 'formik';
import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import {
  Button,
  Buttons,
  CancelButton,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  FieldControl,
  Form,
  FormMessage,
  Radio,
  SingleSelect,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useDocumentTitle, useForm, useIsMounted } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';
import { mergeValues } from '~/utils';

export default function ReportScheduleForm({ reportScheduleId, onDeleted, onSaved, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting }, form] = useForm();
  const toast = useToast();
  const [query, setQuery] = useState({ isReady: false, data: null });

  const fetchData = useCallback(async () => {
    if (!reportScheduleId) {
      setQuery({ isReady: true, data: {} });
      return;
    }

    try {
      const { data } = await api.www.workspaces(workspace.id).reports().schedules(reportScheduleId).get();
      setQuery({ isReady: true, data });
    } catch (error) {
      toast.error(error.message || 'There was a problem reading this report schedule. Please try again.');
    }
  }, [workspace.id, reportScheduleId, api, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  const isNew = !reportScheduleId;
  const title = isNew ? 'New Report Schedule' : 'Edit Report Schedule';

  useDocumentTitle(title);

  const confirmation = useConfirmation();
  const handleDelete = async () => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        resolve={async (confirm) => {
          if (!confirm) {
            resolve(false);
            return;
          }
          await api.www.workspaces(workspace.id).reports().schedules(reportScheduleId).delete();
          await onDeleted();
          await onClose();
          resolve(true);
        }}>
        Are you sure you want to delete this report schedule?
      </DeleteConfirmation>
    ));
  };

  if (!query.isReady) return null;
  if (!query.data) return <ErrorPage.NotFound publicSite={false} />;

  const initialValues = mergeValues(
    {
      reportId: null,
      schedule: {
        frequency: 'monthly',
        dayOfMonth: 1,
      },
      format: 'csv',
      sendEmail: false,
    },
    query.data,
  );

  const validationSchema = Yup.object().shape({
    reportId: Yup.string().uuid().label('Report').required().nullable(),
    schedule: Yup.object().shape({
      frequency: Yup.string().oneOf(['daily', 'weekly', 'monthly']).required(),
      dayOfWeek: Yup.number().when('frequency', {
        is: 'weekly',
        then: Yup.number().min(1).max(7).required(),
        otherwise: Yup.number().nullable(),
      }),
      dayOfMonth: Yup.string().when('frequency', {
        is: 'monthly',
        then: Yup.string()
          .oneOf([...Array(28).keys()].map((i) => (i + 1).toString()).concat(['last']))
          .required(),
        otherwise: Yup.string().nullable(),
      }),
    }),
    format: Yup.string().oneOf(['csv', 'xlsx']).required(),
  });

  return (
    <Drawer
      isOpen
      title={title}
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit();

            const body = _.cloneDeep(values);

            switch (body.schedule.frequency) {
              case 'daily':
                body.schedule.dayOfWeek = null;
                body.schedule.dayOfMonth = null;
                break;

              case 'weekly':
                body.schedule.dayOfMonth = null;
                break;

              case 'monthly':
                body.schedule.dayOfWeek = null;
                break;
            }

            const { data } = reportScheduleId
              ? await api.www.workspaces(workspace.id).reports().schedules(reportScheduleId).update(body)
              : await api.www.workspaces(workspace.id).reports().schedules().create(body);

            await onSaved(data);
            form.save();
            closeDrawer();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={validationSchema}>
            {(formik) => {
              return (
                <Form>
                  <Form.Section title="Report">
                    <Form.Control>
                      <FieldReportSelect name="reportId" placeholder="Report" />
                    </Form.Control>
                  </Form.Section>

                  <Form.Section title="Export Format">
                    <Field.RadioGroup name="format" label="Format">
                      <Radio value="csv" label="CSV" />
                      <Radio value="xlsx" label="XLSX" />
                    </Field.RadioGroup>
                  </Form.Section>

                  <Form.Section
                    title="Schedule"
                    subtitle="Configure the frequency of this report schedule. Reports are generated at 8:30 a.m. in the member's time zone. If the member's time zone is not set, the workspace's time zone will be used.">
                    <Form.Control>
                      <Field.RadioGroup
                        name="schedule.frequency"
                        onChange={({ target: { value } }) => {
                          const values = { ...formik.values.schedule, frequency: value };
                          switch (value) {
                            case 'weekly':
                              values.dayOfWeek = values.dayOfWeek || 1;
                              break;

                            case 'monthly':
                              values.dayOfMonth = values.dayOfMonth || 1;
                          }

                          formik.setFieldValue('schedule', values);
                        }}>
                        <Radio value="daily" label="Daily" />
                        <Radio value="weekly" label="Weekly" />
                        <Radio value="monthly" label="Monthly" />
                      </Field.RadioGroup>
                    </Form.Control>

                    {
                      {
                        weekly: (
                          <Form.Control>
                            <Field.SingleSelect
                              name="schedule.dayOfWeek"
                              placeholder="Select a day of the week"
                              value={formik.values.schedule.dayOfWeek?.toString()}>
                              <option value="1">Monday</option>
                              <option value="2">Tuesday</option>
                              <option value="3">Wednesday</option>
                              <option value="4">Thursday</option>
                              <option value="5">Friday</option>
                              <option value="6">Saturday</option>
                              <option value="7">Sunday</option>
                            </Field.SingleSelect>
                          </Form.Control>
                        ),
                        monthly: (
                          <Form.Control>
                            <Field.SingleSelect name="schedule.dayOfMonth" placeholder="Select a day of the month">
                              {[...Array(28).keys()].map((i) => (
                                <option key={i + 1} value={i + 1}>
                                  {i + 1}
                                </option>
                              ))}
                              <option value="last">Last day of the month</option>
                            </Field.SingleSelect>
                          </Form.Control>
                        ),
                      }[formik.values.schedule.frequency]
                    }
                  </Form.Section>

                  <Form.Section title="Notification">
                    <Form.Control>
                      <Field.Checkbox name="sendEmail" label="Email me when the report is ready" />
                    </Form.Control>
                  </Form.Section>

                  {status && <FormMessage.Error>{message}</FormMessage.Error>}

                  <Drawer.Actions>
                    {!isNew && <DeleteButton onClick={handleDelete}>Delete</DeleteButton>}

                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      <Button isLoading={isSubmitting} onClick={formik.submitForm}>
                        Save &amp; Close
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

function ReportSelect(props) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const [query, setQuery] = useState({ isReady: false, data: [] });
  const [filterValue, setFilterValue] = useState('');
  const isMounted = useIsMounted();

  const options = useMemo(() => {
    let options = query.data;

    if (!filterValue) {
      return options;
    }

    const fuse = new Fuse(options, { keys: ['name'] });
    const results = fuse.search(filterValue);
    return results.map((result) => result.item);
  }, [query.data, filterValue]);

  const selectedOption = useMemo(() => _.find(query.data, { id: props.value }), [query.data, props.value]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).reports().list({ isSchedulable: true });
        if (!isMounted.current) return;
        setQuery({ isReady: true, data });
      } catch {
        // Ignore
      }
    })();
  }, [api, workspace.id, isMounted]);

  return (
    <SingleSelect
      disabled={!query.isReady}
      showFilter={true}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      valueRenderer={selectedOption?.name || ''}
      {...props}>
      {options.map(({ id, name }) => (
        <option key={id} value={id}>
          {name}
        </option>
      ))}
    </SingleSelect>
  );
}

function FieldReportSelect(props) {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <ReportSelect {...field} {...props} />
    </FieldControl>
  );
}
